import React, { useState } from 'react';
import { useMarquerInventorieBulk } from '../hooks/useObjets';

interface BulkInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIds: string[];
}

const BulkInventoryModal: React.FC<BulkInventoryModalProps> = ({ isOpen, onClose, selectedIds }) => {
  const marquerInventorieBulkMutation = useMarquerInventorieBulk();
  const [userId, setUserId] = useState(localStorage.getItem('userId') || '');

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (!userId) {
      alert('Veuillez vous identifier avant de marquer des objets comme inventoriés');
      return;
    }

    try {
      const result = await marquerInventorieBulkMutation.mutateAsync({
        objetIds: selectedIds,
        userId,
      });
      
      // Sauvegarder l'ID utilisateur pour la prochaine fois
      localStorage.setItem('userId', userId);
      
      // Afficher le message de succès avec les infos de l'utilisateur
      const userName = result.user?.name || result.user?.email || userId;
      alert(`✅ ${result.count} objet(s) marqué(s) comme inventorié(s) par ${userName} !`);
      onClose();
    } catch (error: any) {
      console.error('Erreur lors de l\'inventaire en masse:', error);
      const errorMessage = error.response?.data?.message || error.message;
      alert(`❌ Erreur: ${errorMessage}\n\nVeuillez vérifier votre identifiant ou contacter l'administrateur.`);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  ✅ Marquer {selectedIds.length} objet(s) comme inventorié(s)
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Cette action marquera tous les objets sélectionnés comme inventoriés avec la date et l'heure actuelles.
                  </p>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Votre identifiant
                  </label>
                  <input
                    type="text"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="Nom, email ou identifiant (ex: ando.razaka)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Saisissez votre nom, email ou identifiant. Si vous n'êtes pas encore enregistré, un compte sera créé automatiquement.
                  </p>
                </div>

                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-3">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">
                    ℹ️ Informations enregistrées
                  </h4>
                  <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                    <li>Date et heure de l'inventaire</li>
                    <li>Utilisateur ayant effectué le contrôle</li>
                    <li>État de présence confirmé</li>
                  </ul>
                </div>

                <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-3">
                  <p className="text-sm font-medium text-green-800">
                    📋 {selectedIds.length} objet(s) seront marqués comme inventoriés
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={marquerInventorieBulkMutation.isPending || !userId}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {marquerInventorieBulkMutation.isPending ? 'Enregistrement...' : 'Confirmer l\'inventaire'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkInventoryModal;

